import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import React, { useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { useIntl } from "react-intl";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Pagination,
  PaginationItem,
  PaginationLink,
  Table,
  UncontrolledDropdown,
} from "reactstrap";
import {
  formatCompactNumber,
  formatDuration,
  formatWatchTime,
  YoutubeAnalyticsSnapshot,
  YoutubeTimeSeriesDay,
  YoutubeTrendDir,
  YoutubeVideoRow,
} from "./youtubeAnalyticsMock";
import "./YouTubeAnalytics.scss";

interface Props {
  snapshot: YoutubeAnalyticsSnapshot;
}

type TabId =
  | "community"
  | "demographics"
  | "published"
  | "withViews"
  | "competitors";

type VideoColKey = "video" | "date" | "views" | "watchTime" | "likes" | "comments";

const YT_BLUE = "#4285F4";
const YT_GREEN = "#34A853";
const YT_PURPLE = "#9C27B0";
const YT_ORANGE = "#FB8C00";
const YT_PINK = "#E91E63";
const YT_LIGHT_BLUE = "#03A9F4";

const formatKpiNum = (n: number, decimals = 0) =>
  new Intl.NumberFormat("es-CL", {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  }).format(n);

function deltaIcon(dir?: YoutubeTrendDir): IconProp {
  if (dir === "down") return ["fas", "arrow-down"];
  if (dir === "flat") return ["fas", "minus"];
  return ["fas", "arrow-up"];
}

function formatKpiValue(pill: { value: number; decimals?: number; compact?: boolean }) {
  if (pill.compact) return formatCompactNumber(pill.value, pill.decimals ?? 1);
  return formatKpiNum(pill.value, pill.decimals ?? 0);
}

function ColoredKpiCard(props: {
  pill: { value: number; labelKey: string; direction?: YoutubeTrendDir; decimals?: number; compact?: boolean };
  tone: "blue" | "green" | "purple" | "orange" | "pink" | "lightblue";
}) {
  const intl = useIntl();
  const dir = props.pill.direction ?? "up";
  return (
    <div className={cn("yt-kpi-card", `yt-kpi-card--${props.tone}`)}>
      <div className="yt-kpi-card__value">
        {formatKpiValue(props.pill)}
        <FontAwesomeIcon icon={deltaIcon(dir)} className={`yt-kpi-card__arrow yt-kpi-card__arrow--${dir}`} />
      </div>
      <div className="yt-kpi-card__label">{intl.formatMessage({ id: props.pill.labelKey })}</div>
    </div>
  );
}

function useSeries(days: YoutubeTimeSeriesDay[], key: keyof YoutubeTimeSeriesDay): number[] {
  return days.map((d) => Number(d[key] ?? 0) || 0);
}

function YoutubeLineBarChart(props: {
  days: YoutubeTimeSeriesDay[];
  lineVals: number[];
  lineColor: string;
  barVals?: number[];
  barColor?: string;
  yTicks: number[];
  secondLine?: { vals: number[]; color: string };
}) {
  const { days, lineVals, lineColor, barVals, barColor = YT_ORANGE, yTicks, secondLine } = props;
  const n = Math.max(days.length, 2);
  const yMin = Math.min(...yTicks);
  const yMax = Math.max(...yTicks);
  const span = Math.max(1e-6, yMax - yMin);
  const barMax = Math.max(1e-6, Math.max(...(barVals ?? [1]), 1));

  const toPts = (vals: number[]) =>
    vals
      .map((val, i) => {
        const x = (i / (Math.max(vals.length, 2) - 1)) * 100;
        const y = ((yMax - val) / span) * 92 + 4;
        return `${x},${y}`;
      })
      .join(" ");

  return (
    <div className="yt-chart-shell" style={{ "--yt-days": n } as React.CSSProperties}>
      <div className="yt-chart-shell__y">
        {[...yTicks].reverse().map((t) => (
          <span key={t}>{t >= 1000 ? formatCompactNumber(t, 0) : formatKpiNum(t)}</span>
        ))}
      </div>
      <div className="yt-chart-shell__body">
        {barVals ? (
          <div className="yt-chart-shell__bars-row">
            {barVals.map((b, idx) => (
              <div
                key={`b-${days[idx]?.iso}`}
                className="yt-chart-shell__bar"
                style={{
                  height: `${Math.min(100, (b / barMax) * 72)}%`,
                  backgroundColor: barColor,
                  opacity: 0.72,
                }}
              />
            ))}
          </div>
        ) : null}
        <svg className="yt-chart-shell__line-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          {secondLine ? (
            <polyline
              fill="none"
              stroke={secondLine.color}
              strokeWidth={1.1}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              points={toPts(secondLine.vals)}
            />
          ) : null}
          <polyline
            fill="none"
            stroke={lineColor}
            strokeWidth={1.2}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            points={toPts(lineVals)}
          />
        </svg>
      </div>
      <div className="yt-chart-shell__x">
        {days.map((d, i) => (
          <span key={d.iso}>{i % 5 === 0 || i === days.length - 1 ? d.label : ""}</span>
        ))}
      </div>
    </div>
  );
}

function YoutubeMultiLineChart(props: {
  days: YoutubeTimeSeriesDay[];
  series: { vals: number[]; color: string; dash?: string }[];
  barVals?: number[];
  yMax?: number;
}) {
  const n = props.days.length;
  const allVals = props.series.flatMap((s) => s.vals);
  const yMax = props.yMax ?? Math.max(6, ...allVals, 1) * 1.1;
  const barMax = Math.max(1e-6, Math.max(...(props.barVals ?? [1]), 1));

  const toPts = (vals: number[]) =>
    vals
      .map((val, i) => {
        const x = (i / Math.max(n - 1, 1)) * 100;
        const y = ((yMax - val) / yMax) * 90 + 5;
        return `${x},${y}`;
      })
      .join(" ");

  return (
    <div className="yt-chart-shell" style={{ "--yt-days": n } as React.CSSProperties}>
      <div className="yt-chart-shell__y">
        {[yMax, yMax * 0.66, yMax * 0.33, 0].map((t) => (
          <span key={t}>{t >= 1000 ? formatCompactNumber(t, 0) : formatKpiNum(Math.round(t))}</span>
        ))}
      </div>
      <div className="yt-chart-shell__body">
        {props.barVals ? (
          <div className="yt-chart-shell__bars-row">
            {props.barVals.map((b, idx) => (
              <div
                key={`mb-${props.days[idx]?.iso}`}
                className="yt-chart-shell__bar"
                style={{
                  height: `${Math.min(100, (b / barMax) * 62)}%`,
                  backgroundColor: YT_ORANGE,
                  opacity: 0.65,
                }}
              />
            ))}
          </div>
        ) : null}
        <svg className="yt-chart-shell__line-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          {props.series.map((s, i) => (
            <polyline
              key={i}
              fill="none"
              stroke={s.color}
              strokeWidth={i === 0 ? 1.2 : 1}
              strokeDasharray={s.dash}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              points={toPts(s.vals)}
            />
          ))}
        </svg>
      </div>
      <div className="yt-chart-shell__x">
        {props.days.map((d, i) => (
          <span key={d.iso}>{i % 5 === 0 || i === n - 1 ? d.label : ""}</span>
        ))}
      </div>
    </div>
  );
}

function DonutChart(props: {
  slices: { label: string; pct: number; color: string }[];
  size?: number;
}) {
  const total = props.slices.reduce((s, x) => s + x.pct, 0) || 100;
  let acc = 0;
  const gradient = props.slices
    .map((s) => {
      const start = (acc / total) * 100;
      acc += s.pct;
      const end = (acc / total) * 100;
      return `${s.color} ${start}% ${end}%`;
    })
    .join(", ");

  return (
    <div className="yt-donut-wrap">
      <div
        className="yt-donut"
        style={{
          width: props.size ?? 140,
          height: props.size ?? 140,
          background: `conic-gradient(${gradient})`,
        }}
      />
      <ul className="yt-donut-legend">
        {props.slices.map((s) => (
          <li key={s.label}>
            <span className="yt-donut-legend__swatch" style={{ background: s.color }} />
            {s.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AgeBarChart(props: { bars: { label: string; value: number }[] }) {
  const max = Math.max(...props.bars.map((b) => b.value), 1);
  return (
    <div className="yt-age-chart">
      <div className="yt-age-chart__bars">
        {props.bars.map((b) => (
          <div key={b.label} className="yt-age-chart__col">
            <div
              className="yt-age-chart__bar"
              style={{ height: `${(b.value / max) * 100}%` }}
            />
            <span className="yt-age-chart__label">{b.label}</span>
          </div>
        ))}
      </div>
      <div className="yt-age-chart__y">
        {[max, Math.round(max * 0.66), Math.round(max * 0.33), 0].map((t) => (
          <span key={t}>{t}</span>
        ))}
      </div>
    </div>
  );
}

function VideoListSection(props: {
  rows: YoutubeVideoRow[];
  titleKey: string;
}) {
  const intl = useIntl();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [visibleCols, setVisibleCols] = useState<Record<VideoColKey, boolean>>({
    video: true,
    date: true,
    views: true,
    watchTime: true,
    likes: true,
    comments: true,
  });

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return props.rows;
    return props.rows.filter((r) => r.title.toLowerCase().includes(ql));
  }, [props.rows, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  const csvData = filtered.map((r) => ({
    title: r.title,
    date: `${r.dateLabel} ${r.timeLabel}`,
    views: r.views,
    watchTime: formatWatchTime(r.watchTimeHours),
    likes: r.likes,
    comments: r.comments,
  }));

  const toggleCol = (key: VideoColKey) =>
    setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <section className="yt-analytics-card yt-video-list">
      <h3 className="yt-analytics-card__title">
        {intl.formatMessage({ id: props.titleKey })}
      </h3>
      <div className="yt-video-list__toolbar">
        <InputGroup className="yt-video-list__search">
          <InputGroupAddon addonType="prepend">
            <InputGroupText>
              <FontAwesomeIcon icon={["fas", "search"]} />
            </InputGroupText>
          </InputGroupAddon>
          <Input
            placeholder={intl.formatMessage({ id: "app.youtubeAnalytics.searchPlaceholder" })}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </InputGroup>
        <div className="yt-video-list__actions">
          <UncontrolledDropdown>
            <DropdownToggle color="light" className="yt-toolbar-btn">
              <FontAwesomeIcon icon={["fas", "columns"]} className="mr-1" />
              {intl.formatMessage({ id: "app.youtubeAnalytics.columnsMenu" })}
            </DropdownToggle>
            <DropdownMenu right>
              {(Object.keys(visibleCols) as VideoColKey[]).map((key) => (
                <DropdownItem key={key} toggle={false} onClick={() => toggleCol(key)}>
                  <FontAwesomeIcon
                    icon={visibleCols[key] ? ["fas", "check-square"] : ["far", "square"]}
                    className="mr-2"
                  />
                  {intl.formatMessage({ id: `app.youtubeAnalytics.col.${key}` })}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledDropdown>
          <CSVLink
            className="btn btn-light yt-toolbar-btn"
            data={csvData}
            filename="youtube-videos.csv"
            target="_blank"
          >
            <FontAwesomeIcon icon={["fas", "file-csv"]} className="mr-1" />
            {intl.formatMessage({ id: "app.youtubeAnalytics.downloadCsv" })}
          </CSVLink>
        </div>
      </div>
      <div className="table-responsive">
        <Table className="yt-videos-table mb-0" hover>
          <thead>
            <tr>
              {visibleCols.video && (
                <th>{intl.formatMessage({ id: "app.youtubeAnalytics.tableVideo" })}</th>
              )}
              {visibleCols.date && (
                <th>{intl.formatMessage({ id: "app.youtubeAnalytics.tableDate" })}</th>
              )}
              {visibleCols.views && (
                <th className="text-right">
                  {intl.formatMessage({ id: "app.youtubeAnalytics.videoViews" })}
                </th>
              )}
              {visibleCols.watchTime && (
                <th className="text-right">
                  {intl.formatMessage({ id: "app.youtubeAnalytics.watchTime" })}
                </th>
              )}
              {visibleCols.likes && (
                <th className="text-right">
                  {intl.formatMessage({ id: "app.youtubeAnalytics.likes" })}
                </th>
              )}
              {visibleCols.comments && (
                <th className="text-right">
                  {intl.formatMessage({ id: "app.youtubeAnalytics.comments" })}
                </th>
              )}
              <th aria-hidden style={{ width: 32 }} />
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => (
              <tr key={row.id}>
                {visibleCols.video && (
                  <td>
                    <div className="d-flex align-items-center">
                      <div
                        className="yt-video-thumb mr-2"
                        style={
                          row.thumbnailUrl
                            ? undefined
                            : { background: row.thumbnailColor }
                        }
                      >
                        {row.thumbnailUrl ? (
                          <img
                            src={row.thumbnailUrl}
                            alt=""
                            loading="lazy"
                            className="yt-video-thumb__img"
                          />
                        ) : null}
                      </div>
                      <span className="yt-video-title">{row.title}</span>
                    </div>
                  </td>
                )}
                {visibleCols.date && (
                  <td className="yt-video-date">
                    <div>{row.dateLabel}</div>
                    <small className="text-muted">{row.timeLabel}</small>
                  </td>
                )}
                {visibleCols.views && (
                  <td className="text-right">{formatKpiNum(row.views)}</td>
                )}
                {visibleCols.watchTime && (
                  <td className="text-right">{formatWatchTime(row.watchTimeHours)}</td>
                )}
                {visibleCols.likes && (
                  <td className="text-right">{formatKpiNum(row.likes)}</td>
                )}
                {visibleCols.comments && (
                  <td className="text-right">{formatKpiNum(row.comments)}</td>
                )}
                <td>
                  <button type="button" className="yt-row-menu-btn" aria-label="Opciones">
                    <FontAwesomeIcon icon={["fas", "ellipsis-v"]} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <div className="yt-video-list__footer">
        <div className="d-flex align-items-center">
          <span className="mr-2 small text-muted">
            {intl.formatMessage({ id: "app.youtubeAnalytics.perPage" })}
          </span>
          <Input
            type="select"
            bsSize="sm"
            style={{ width: 70 }}
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {[5, 10, 20].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Input>
        </div>
        <span className="small text-muted">
          {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, filtered.length)}{" "}
          {intl.formatMessage({ id: "app.youtubeAnalytics.of" })} {filtered.length}
        </span>
        <Pagination size="sm" className="mb-0">
          <PaginationItem disabled={page <= 1}>
            <PaginationLink first onClick={() => setPage(1)} />
          </PaginationItem>
          <PaginationItem disabled={page <= 1}>
            <PaginationLink previous onClick={() => setPage((p) => Math.max(1, p - 1))} />
          </PaginationItem>
          <PaginationItem disabled={page >= totalPages}>
            <PaginationLink next onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />
          </PaginationItem>
          <PaginationItem disabled={page >= totalPages}>
            <PaginationLink last onClick={() => setPage(totalPages)} />
          </PaginationItem>
        </Pagination>
      </div>
    </section>
  );
}

export const YouTubeAnalyticsDashboard = ({ snapshot }: Props) => {
  const intl = useIntl();
  const [tab, setTab] = useState<TabId>("community");
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const growthSeries = snapshot.community.growthSeries;
  const balanceSeries = snapshot.community.balanceSeries;

  const subsLine = useSeries(growthSeries, "subscribersTotal");
  const viewsLine = useSeries(growthSeries, "views");
  const videosBar = useSeries(growthSeries, "videosPosted");
  const gainedLine = useSeries(balanceSeries, "subscribersGained");
  const lostLine = useSeries(balanceSeries, "subscribersLost");

  const subsTicks = useMemo(() => {
    const lo = Math.min(...subsLine);
    const hi = Math.max(...subsLine);
    const pad = (hi - lo) * 0.08 || 500;
    const span = hi - lo + pad * 2 || 1;
    return [lo - pad, lo - pad + span / 3, lo - pad + (span * 2) / 3, hi + pad];
  }, [subsLine]);

  const balanceTicks = useMemo(() => {
    const hi = Math.max(...gainedLine, ...lostLine, 1);
    return [0, hi * 0.33, hi * 0.66, hi];
  }, [gainedLine, lostLine]);

  const publishedViews = useSeries(snapshot.publishedVideos.series, "views");
  const publishedLikes = useSeries(snapshot.publishedVideos.series, "likes");
  const publishedDislikes = useSeries(snapshot.publishedVideos.series, "dislikes");
  const publishedComments = useSeries(snapshot.publishedVideos.series, "comments");
  const publishedShares = useSeries(snapshot.publishedVideos.series, "shares");
  const publishedVideosBar = useSeries(snapshot.publishedVideos.series, "videosPosted");

  const withViewsViews = useSeries(snapshot.videosWithViews.series, "views");
  const withViewsLikes = useSeries(snapshot.videosWithViews.series, "likes");
  const withViewsComments = useSeries(snapshot.videosWithViews.series, "comments");
  const withViewsShares = useSeries(snapshot.videosWithViews.series, "shares");

  const tabs: { id: TabId; labelKey: string }[] = [
    { id: "community", labelKey: "app.youtubeAnalytics.tabCommunity" },
    { id: "demographics", labelKey: "app.youtubeAnalytics.tabDemographics" },
    { id: "published", labelKey: "app.youtubeAnalytics.tabPublished" },
    { id: "withViews", labelKey: "app.youtubeAnalytics.tabWithViews" },
    { id: "competitors", labelKey: "app.youtubeAnalytics.tabCompetitors" },
  ];

  const renderEngagementSummary = (summary: typeof snapshot.publishedVideos.summary) => (
    <div className="yt-kpi-row">
      <ColoredKpiCard pill={summary.views} tone="green" />
      <ColoredKpiCard pill={summary.likes} tone="lightblue" />
      <ColoredKpiCard pill={summary.dislikes} tone="pink" />
      <ColoredKpiCard pill={summary.comments} tone="purple" />
      <ColoredKpiCard pill={summary.shares} tone="blue" />
      <ColoredKpiCard pill={summary.videos} tone="orange" />
    </div>
  );

  return (
    <div className="yt-analytics-root">
      {!bannerDismissed && (
        <div className="yt-analytics-banner">
          <FontAwesomeIcon icon={["fas", "info-circle"]} className="mr-2" />
          <span>{intl.formatMessage({ id: "app.youtubeAnalytics.banner" })}</span>
          <button
            type="button"
            className="yt-analytics-banner__close"
            onClick={() => setBannerDismissed(true)}
            aria-label="Cerrar"
          >
            <FontAwesomeIcon icon={["fas", "times"]} />
          </button>
        </div>
      )}

      <div className="yt-analytics-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={cn("yt-analytics-tab", tab === t.id && "yt-analytics-tab--active")}
            onClick={() => setTab(t.id)}
          >
            {intl.formatMessage({ id: t.labelKey })}
          </button>
        ))}
      </div>

      {tab === "community" && (
        <>
          <section className="yt-analytics-card">
            <div className="yt-analytics-card__head">
              <div>
                <h2>{intl.formatMessage({ id: "app.youtubeAnalytics.sectionCommunity" })}</h2>
              </div>
              <div className="yt-channel-chip">
                <img src={snapshot.channelAvatarUrl} alt="" className="yt-channel-chip__avatar" />
                <FontAwesomeIcon icon={["fab", "youtube"]} className="text-danger" />
                <span>{snapshot.channelDisplayName}</span>
              </div>
            </div>

            <h3 className="yt-section-subtitle">
              {intl.formatMessage({ id: "app.youtubeAnalytics.sectionGrowth" })}
            </h3>
            <div className="yt-kpi-row yt-kpi-row--4">
              <ColoredKpiCard pill={snapshot.community.growth.subscribers} tone="blue" />
              <ColoredKpiCard pill={snapshot.community.growth.views} tone="green" />
              <ColoredKpiCard pill={snapshot.community.growth.revenue} tone="purple" />
              <ColoredKpiCard pill={snapshot.community.growth.videos} tone="orange" />
            </div>
            <YoutubeLineBarChart
              days={growthSeries}
              lineVals={subsLine}
              lineColor={YT_BLUE}
              secondLine={{ vals: viewsLine, color: YT_GREEN }}
              barVals={videosBar}
              yTicks={subsTicks}
            />
          </section>

          <section className="yt-analytics-card">
            <h3 className="yt-section-subtitle">
              {intl.formatMessage({ id: "app.youtubeAnalytics.sectionBalance" })}
            </h3>
            <div className="yt-kpi-row yt-kpi-row--3">
              <ColoredKpiCard pill={snapshot.community.balance.gained} tone="blue" />
              <ColoredKpiCard pill={snapshot.community.balance.lost} tone="pink" />
              <ColoredKpiCard pill={snapshot.community.balance.videos} tone="orange" />
            </div>
            <YoutubeLineBarChart
              days={balanceSeries}
              lineVals={gainedLine}
              lineColor={YT_BLUE}
              secondLine={{ vals: lostLine, color: YT_PINK }}
              barVals={videosBar}
              yTicks={balanceTicks}
            />
          </section>

          <div className="yt-analytics-split">
            <section className="yt-analytics-card">
              <div className="yt-analytics-card__head">
                <h3 className="mb-0">
                  {intl.formatMessage({ id: "app.youtubeAnalytics.viewersByCountry" })}
                </h3>
                <button type="button" className="yt-link-btn">
                  <FontAwesomeIcon icon={["fas", "table"]} className="mr-1" />
                  {intl.formatMessage({ id: "app.youtubeAnalytics.viewTable" })}
                </button>
              </div>
              <DonutChart
                slices={snapshot.community.viewersByCountry.map((c) => ({
                  label: intl.formatMessage({ id: c.countryKey }),
                  pct: c.pct,
                  color: c.color,
                }))}
              />
            </section>
            <section className="yt-analytics-card">
              <h3 className="yt-analytics-card__title">
                {intl.formatMessage({ id: "app.youtubeAnalytics.trafficSources" })}
              </h3>
              <Table className="yt-traffic-table mb-0" size="sm">
                <thead>
                  <tr>
                    <th>{intl.formatMessage({ id: "app.youtubeAnalytics.trafficSource" })}</th>
                    <th className="text-right">
                      {intl.formatMessage({ id: "app.youtubeAnalytics.visitors" })}
                    </th>
                    <th className="text-right">
                      {intl.formatMessage({ id: "app.youtubeAnalytics.percentage" })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {snapshot.community.trafficSources.map((row) => (
                    <tr key={row.sourceKey}>
                      <td>{intl.formatMessage({ id: row.sourceKey })}</td>
                      <td className="text-right">{formatKpiNum(row.visitors)}</td>
                      <td className="text-right">{row.pct.toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </section>
          </div>
        </>
      )}

      {tab === "demographics" && (
        <>
          <section className="yt-analytics-card">
            <h3 className="yt-analytics-card__title">
              {intl.formatMessage({ id: "app.youtubeAnalytics.sectionDemographics" })}
            </h3>
            <div className="yt-analytics-split">
              <div>
                <div className="yt-analytics-card__head mb-2">
                  <h4 className="mb-0">{intl.formatMessage({ id: "app.youtubeAnalytics.gender" })}</h4>
                  <button type="button" className="yt-link-btn">
                    <FontAwesomeIcon icon={["fas", "table"]} className="mr-1" />
                    {intl.formatMessage({ id: "app.youtubeAnalytics.viewTable" })}
                  </button>
                </div>
                <DonutChart
                  slices={snapshot.demographics.gender.map((g) => ({
                    label: intl.formatMessage({ id: g.labelKey }),
                    pct: g.pct,
                    color: g.color,
                  }))}
                />
              </div>
              <div>
                <h4>{intl.formatMessage({ id: "app.youtubeAnalytics.age" })}</h4>
                <AgeBarChart
                  bars={snapshot.demographics.age.map((a) => ({
                    label: intl.formatMessage({ id: a.rangeKey }),
                    value: a.value,
                  }))}
                />
              </div>
            </div>
          </section>
          <div className="yt-analytics-split">
            <section className="yt-analytics-card">
              <div className="yt-analytics-card__head">
                <h3 className="mb-0">
                  {intl.formatMessage({ id: "app.youtubeAnalytics.viewersByCountry" })}
                </h3>
                <button type="button" className="yt-link-btn">
                  <FontAwesomeIcon icon={["fas", "table"]} className="mr-1" />
                  {intl.formatMessage({ id: "app.youtubeAnalytics.viewTable" })}
                </button>
              </div>
              <DonutChart
                slices={snapshot.community.viewersByCountry.map((c) => ({
                  label: intl.formatMessage({ id: c.countryKey }),
                  pct: c.pct,
                  color: c.color,
                }))}
              />
            </section>
            <section className="yt-analytics-card">
              <h3 className="yt-analytics-card__title">
                {intl.formatMessage({ id: "app.youtubeAnalytics.trafficSources" })}
              </h3>
              <Table className="yt-traffic-table mb-0" size="sm">
                <thead>
                  <tr>
                    <th>{intl.formatMessage({ id: "app.youtubeAnalytics.trafficSource" })}</th>
                    <th className="text-right">
                      {intl.formatMessage({ id: "app.youtubeAnalytics.visitors" })}
                    </th>
                    <th className="text-right">
                      {intl.formatMessage({ id: "app.youtubeAnalytics.percentage" })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {snapshot.community.trafficSources.map((row) => (
                    <tr key={row.sourceKey}>
                      <td>{intl.formatMessage({ id: row.sourceKey })}</td>
                      <td className="text-right">{formatKpiNum(row.visitors)}</td>
                      <td className="text-right">{row.pct.toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </section>
          </div>
        </>
      )}

      {tab === "published" && (
        <>
          <section className="yt-analytics-card">
            <h3 className="yt-analytics-card__title">
              {intl.formatMessage({ id: "app.youtubeAnalytics.sectionPublished" })}
            </h3>
            {renderEngagementSummary(snapshot.publishedVideos.summary)}
            <YoutubeMultiLineChart
              days={snapshot.publishedVideos.series}
              barVals={publishedVideosBar}
              series={[
                { vals: publishedViews, color: YT_GREEN },
                { vals: publishedLikes, color: YT_LIGHT_BLUE },
                { vals: publishedDislikes, color: YT_PINK },
                { vals: publishedComments, color: YT_PURPLE, dash: "3 3" },
                { vals: publishedShares, color: YT_BLUE, dash: "5 4" },
              ]}
            />
          </section>
          <VideoListSection rows={snapshot.videosTable} titleKey="app.youtubeAnalytics.videoList" />
        </>
      )}

      {tab === "withViews" && (
        <>
          <section className="yt-analytics-card">
            <h3 className="yt-analytics-card__title">
              {intl.formatMessage({ id: "app.youtubeAnalytics.sectionWithViews" })}
            </h3>
            {renderEngagementSummary(snapshot.videosWithViews.summary)}
            <YoutubeMultiLineChart
              days={snapshot.videosWithViews.series}
              series={[
                { vals: withViewsViews, color: YT_GREEN },
                { vals: withViewsLikes, color: YT_LIGHT_BLUE },
                { vals: withViewsComments, color: YT_PURPLE, dash: "3 3" },
                { vals: withViewsShares, color: YT_PINK, dash: "5 4" },
              ]}
            />
          </section>
          <VideoListSection rows={snapshot.videosTable} titleKey="app.youtubeAnalytics.videoList" />
        </>
      )}

      {tab === "competitors" && (
        <section className="yt-analytics-card">
          <h3 className="yt-analytics-card__title">
            {intl.formatMessage({ id: "app.youtubeAnalytics.tabCompetitors" })}
          </h3>
          <div className="table-responsive">
            <Table className="yt-videos-table mb-0" hover>
              <thead>
                <tr>
                  <th>{intl.formatMessage({ id: "app.youtubeAnalytics.competitorChannel" })}</th>
                  <th className="text-right">
                    {intl.formatMessage({ id: "app.youtubeAnalytics.subscribers" })}
                  </th>
                  <th className="text-right">
                    {intl.formatMessage({ id: "app.youtubeAnalytics.playbacks" })}
                  </th>
                  <th className="text-right">
                    {intl.formatMessage({ id: "app.youtubeAnalytics.videosCount" })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {snapshot.competitors.rows.map((row) => (
                  <tr key={row.id} className={row.id === "self" ? "yt-competitor-row--self" : undefined}>
                    <td className="font-weight-bold">{row.name}</td>
                    <td className="text-right">{formatCompactNumber(row.subscribers, 1)}</td>
                    <td className="text-right">{formatCompactNumber(row.views, 1)}</td>
                    <td className="text-right">{formatKpiNum(row.videos)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </section>
      )}
    </div>
  );
};
